import {
  Calendar,
  FileText,
  Phone,
  PhoneCall,
  Users,
  Video,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import type {
  InteractionType,
  NextActionType,
  OutcomeSignal,
} from "@/api/types";
import type { PendingAttachment } from "@/components/attachments/AttachmentUpload";

export interface OutcomeDef {
  id: OutcomeSignal;
  label: string;
  kind: "positive" | "neutral" | "risk";
  /** §D: forceNext outcomes — nextAction.actionType required */
  forceNext?: boolean;
  /** §A: risk outcomes — resultSummary required server-side */
  summaryRequired?: boolean;
  /** §C: outcome auto-suggests an actionType (override allowed unless forced) */
  suggest?: NextActionType;
}

export const OUTCOMES: OutcomeDef[] = [
  { id: "agreed", label: "Домовились про наступний крок", kind: "positive", forceNext: true },
  { id: "followup", label: "Потрібен follow-up", kind: "neutral", forceNext: true },
  { id: "interested", label: "Клієнт зацікавлений", kind: "positive" },
  { id: "notready", label: "Клієнт не готовий", kind: "neutral" },
  { id: "complaint", label: "Проблема / скарга", kind: "risk", forceNext: true, summaryRequired: true },
  { id: "contract", label: "Питання по договору", kind: "neutral", suggest: "perevirka_dogovir" },
  { id: "payment", label: "Питання по оплаті / боргу", kind: "risk", forceNext: true, summaryRequired: true, suggest: "perevirka_oplata" },
  { id: "escalate", label: "Потрібно залучити керівника", kind: "risk", forceNext: true, summaryRequired: true, suggest: "kerivnyk" },
];

export const OUTCOME_GROUPS: Array<{ kind: OutcomeDef["kind"]; label: string }> = [
  { kind: "positive", label: "Позитивні" },
  { kind: "neutral", label: "Нейтральні" },
  { kind: "risk", label: "Потребує уваги" },
];

export interface NextActionDef {
  id: NextActionType;
  label: string;
  icon: LucideIcon;
}

export const NEXT_ACTIONS: NextActionDef[] = [
  { id: "callback", label: "Передзвонити", icon: PhoneCall },
  { id: "kp", label: "Надіслати КП", icon: FileText },
  { id: "perevirka_oplata", label: "Перевірити оплату", icon: Wallet },
  { id: "perevirka_dogovir", label: "Перевірити договір", icon: FileText },
  { id: "kerivnyk", label: "Залучити керівника", icon: Users },
  { id: "meeting", label: "Зустріч", icon: Calendar },
  { id: "other", label: "Інше", icon: FileText },
];

export const INTERACTION_TYPES: Array<{
  id: InteractionType;
  label: string;
  icon: LucideIcon;
}> = [
  { id: "meeting", label: "Зустріч", icon: Users },
  { id: "call", label: "Дзвінок", icon: Phone },
  { id: "video", label: "Відео", icon: Video },
];

export const REMINDER_CHOICES: Array<{ id: "15m" | "1h" | "1d" | "none"; label: string }> = [
  { id: "15m", label: "за 15 хв" },
  { id: "1h", label: "за 1 год" },
  { id: "1d", label: "за 1 день" },
  { id: "none", label: "без" },
];

export const DURATION_CHOICES = [15, 30, 60, 90] as const;

export function findOutcome(id: OutcomeSignal | null): OutcomeDef | undefined {
  return id ? OUTCOMES.find((o) => o.id === id) : undefined;
}

export function nextActionLabel(id: NextActionType | null): string {
  return NEXT_ACTIONS.find((a) => a.id === id)?.label ?? "Наступна дія";
}

/** Compose YYYY-MM-DD HH:MM:SS from date + time inputs. */
export function composePlannedAt(date: string, time: string): string {
  if (!date || !time) return "";
  const t = time.length === 5 ? `${time}:00` : time;
  return `${date} ${t}`;
}

/** Map matrix-§3 quick-pick → due date (YYYY-MM-DD). */
export function quickDueToDate(quick: "today" | "tomorrow"): string {
  const d = new Date();
  if (quick === "tomorrow") d.setDate(d.getDate() + 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export interface ReportDraft {
  occurred: boolean | null;
  noReason: "cancelled" | "noshow" | "other" | null;
  noReasonText: string;
  outcome: OutcomeSignal | null;
  summary: string;
  needNext: boolean | null;
  actionType: NextActionType | null;
  actionDetail: string;
  dueDate: string;
  dueQuick: "today" | "tomorrow" | "custom" | null;
  /**
   * TASK-ATT v1.3 (W3) — staged attachment ids bound on F2 submit. Full merged
   * id list emitted by AttachmentUpload.onChange; sent verbatim as
   * InteractionReportPayload.attachmentsIds (link-multiple REPLACES the set).
   */
  attachmentsIds: string[];
  /** Local uploaded-but-not-persisted queue, round-tripped through the draft. */
  pendingAttachments: PendingAttachment[];
}

export const EMPTY_DRAFT: ReportDraft = {
  occurred: null,
  noReason: null,
  noReasonText: "",
  outcome: null,
  summary: "",
  needNext: null,
  actionType: null,
  actionDetail: "",
  dueDate: "",
  dueQuick: null,
  attachmentsIds: [],
  pendingAttachments: [],
};
